import React, { useContext, Fragment } from "react";
import { RouteFilterContext } from "./context/RouteFilterContext";
import { useDeviceLayout } from "../common/context/DeviceLayoutContext";
import Row from "@amzn/meridian/row";
import RouteGroupedStatusView from "./RouteGroupedStatusView";
import { RouteStatusContext } from "./context/RouteStatusContext";
import { applyProductTypeFilter } from "./utils/Util";
import {
  ROUTES_AT_RISK,
  NOT_STARTED,
  PROGRESSING,
  DONE,
  STOW_PENDING,
  STAGE_PENDING,
  STAGE_IN_PROGRESS,
  STAGED,
  STOWED,
  PICK_PENDING,
  PICK_IN_PROGRESS,
  TOTAL_GROCERY,
  TOTAL_ROUTES,
  TOTAL_CORE,
  PENDING,
  IN_PROGRESS,
  isDropToCartEnabled,
  STOW,
  STAGE,
  PICK,
} from "./utils/EntityStatusManager";
import {
  getRouteStatusMetricCount,
  PRODUCT_TYPES,
} from "./utils/ProductTypeUtils";
import {
  applyGlobalStageLocationTypeFilter,
  filterOption,
} from "./utils/FilterUtil";
import useCapabilityContext from "./context/useCapabilityContext";
import {
  STATUS,
  ROUTES,
  PICKLISTS,
  CLUSTER_FILTER,
  ROUTE_CODE_FILTER,
  DRIVER_TYPE_FILTER,
  DEPARTURE_FILTER,
  ROUTE_SLA_FILTER,
  STATUS_FILTER,
  ROUTE_STATUS_FILTER,
} from "./utils/Constants";

const RouteOverallStatus = () => {
  const { viewModel } = useContext(RouteStatusContext);
  const { viewModelF, dispatchF } = useContext(RouteFilterContext);
  const { configViewModelRef } = useCapabilityContext();
  const { spacingInset_shorthand4 } = useDeviceLayout();
  let routeList = Array.from(viewModel.routeList);

  // Apply all filters including product type
  const applyAllFilters = (route) => {
    return (
      applyProductTypeFilter(viewModelF, route) &&
      filterOption(route, CLUSTER_FILTER, ROUTE_CODE_FILTER, viewModelF) &&
      filterOption(route, DRIVER_TYPE_FILTER, DRIVER_TYPE_FILTER, viewModelF) &&
      filterOption(route, DEPARTURE_FILTER, ROUTE_SLA_FILTER, viewModelF) &&
      filterOption(route, STATUS_FILTER, ROUTE_STATUS_FILTER, viewModelF) &&
      applyGlobalStageLocationTypeFilter(route, viewModelF.stageLocationType)
    );
  };

  routeList = routeList.filter(applyAllFilters);
  let routeStatusMetrics = getRouteStatusMetricCount(
    routeList,
    viewModelF.productType,
  );
  return (
    <Row
      spacingInset={spacingInset_shorthand4}
      spacing="medium"
      alignmentVertical="stretch"
    >
      <Row alignmentVertical="center" width="100%" spacing="medium">
        <RouteGroupedStatusView
          title={
            viewModelF.productType === PRODUCT_TYPES.ALL_ROUTES
              ? ROUTES
              : PICKLISTS
          }
          statusItems={[
            {
              label:
                viewModelF.productType === PRODUCT_TYPES.ALL_ROUTES
                  ? TOTAL_ROUTES
                  : viewModelF.productType === PRODUCT_TYPES.CORE_PICKLIST
                    ? TOTAL_CORE
                    : TOTAL_GROCERY,
              value: routeList.length,
              statusTypes: [],
            },
            {
              label: ROUTES_AT_RISK,
              value: routeStatusMetrics.isAtRisk,
              statusTypes: [ROUTES_AT_RISK],
            },
          ]}
          dispatchF={dispatchF}
        />

        {viewModelF.productType === PRODUCT_TYPES.ALL_ROUTES ? (
          <RouteGroupedStatusView
            title={STATUS}
            statusItems={[
              {
                label: NOT_STARTED,
                value: routeStatusMetrics.notStarted,
                statusTypes: [NOT_STARTED],
              },
              {
                label: PROGRESSING,
                value: routeStatusMetrics.progressing,
                statusTypes: [PROGRESSING],
              },
              {
                label: DONE,
                value: routeStatusMetrics.done,
                statusTypes: [DONE],
              },
            ]}
            dispatchF={dispatchF}
          />
        ) : viewModelF.productType === PRODUCT_TYPES.GROCERY_PICKLIST ? (
          <Fragment>
            <RouteGroupedStatusView
              title={STOW}
              statusItems={[
                {
                  label: PENDING,
                  value: routeStatusMetrics.stowPending,
                  statusTypes: [STOW_PENDING],
                },
                {
                  label: STOWED,
                  value: routeStatusMetrics.stowed,
                  statusTypes: [STOWED],
                },
              ]}
              dispatchF={dispatchF}
            />
            <RouteGroupedStatusView
              title={PICK}
              statusItems={[
                {
                  label: PENDING,
                  value: routeStatusMetrics.pickPending,
                  statusTypes: [PICK_PENDING],
                },
                {
                  label: IN_PROGRESS,
                  value: routeStatusMetrics.pickInProgress,
                  statusTypes: [PICK_IN_PROGRESS],
                },
              ]}
              dispatchF={dispatchF}
            />
            <RouteGroupedStatusView
              title={STAGE}
              statusItems={[
                {
                  label: IN_PROGRESS,
                  value: routeStatusMetrics.stageInProgress,
                  statusTypes: [STAGE_IN_PROGRESS],
                },
                {
                  label: STAGED,
                  value: routeStatusMetrics.staged,
                  statusTypes: [STAGED],
                },
              ]}
              dispatchF={dispatchF}
            />
          </Fragment>
        ) : (
          <Fragment>
            <RouteGroupedStatusView
              title={STOW}
              statusItems={[
                {
                  label: PENDING,
                  value: routeStatusMetrics.stowPending,
                  statusTypes: [STOW_PENDING],
                },
              ]}
              dispatchF={dispatchF}
            />
            {isDropToCartEnabled(configViewModelRef.current.stationConfig) && (
              <RouteGroupedStatusView
                title={PICK}
                statusItems={[
                  {
                    label: PENDING,
                    value: routeStatusMetrics.pickPending,
                    statusTypes: [PICK_PENDING],
                  },
                  {
                    label: IN_PROGRESS,
                    value: routeStatusMetrics.pickInProgress,
                    statusTypes: [PICK_IN_PROGRESS],
                  },
                ]}
                dispatchF={dispatchF}
              />
            )}
            <RouteGroupedStatusView
              title={STAGE}
              statusItems={[
                {
                  label: PENDING,
                  value: routeStatusMetrics.stagePending,
                  statusTypes: [STAGE_PENDING],
                },
                {
                  label: IN_PROGRESS,
                  value: routeStatusMetrics.stageInProgress,
                  statusTypes: [STAGE_IN_PROGRESS],
                },
                {
                  label: STAGED,
                  value: routeStatusMetrics.staged,
                  statusTypes: [STAGED],
                },
              ]}
              dispatchF={dispatchF}
            />
          </Fragment>
        )}
      </Row>
    </Row>
  );
};
export default RouteOverallStatus;



// WEBPACK FOOTER //
// ./src/components/RouteStatus/RouteOverallStatus.js